/**
 * Pricing and the plan comparison, below the fold on the auth gate —
 * design/PROTOTYPE-SPEC.md §1.
 *
 * ## Why this exists at all
 *
 * Until now a prospect had to sign up before they could find out what anything
 * cost. That is fine for a product people already know; it is not fine for one
 * a clinic is deciding about. This is the page that has to answer "what does it
 * do, what does it cost, what happens after the trial" before anyone types an
 * email address.
 *
 * ## Prices come from `packages/core`, which is a cache of Stripe
 *
 * Same source the in-app plan picker uses, so the number a prospect is quoted
 * and the number they are charged cannot drift apart without
 * `pnpm check:pricing` failing. If they ever disagree, this is the bug and
 * Stripe is right.
 *
 * ## ⚠️ Barcode scanning must never appear here
 *
 * Several pricing screenshots in `docs/design-reference/` advertise
 * "Barcode / camera add-stock". It was **cut outright** on 12 August 2026
 * (`docs/REMAINING-SCOPE.md` §F) — advertising it would be selling something
 * that does not exist and is not planned. Same for the AI marketing assistant
 * and Plan-ahead, which are built-but-unshipped and unbuilt respectively.
 */
import {
  PLANS,
  PLAN_LABEL,
  PRICING,
  PRODUCT_LIMIT,
  SEAT_LIMIT,
  TRIAL_DAYS,
  yearlySaving,
  type Plan,
} from '@inventory-tracker/core';
import { useState } from 'react';
import { c, sans, segTab, segTrack } from '../theme.js';
import { money } from '../app/format.js';

type Cycle = 'monthly' | 'yearly';

/** Headline features per plan, in the order a clinic cares about them. */
export const HIGHLIGHTS: Record<Plan, string[]> = {
  starter: [
    'Batch and expiry tracking with FEFO',
    'Stock counts and a full movement ledger',
    'Spreadsheet import and export',
  ],
  pro: [
    'Everything in Starter',
    'Analytics: where the money went, days of cover, waste, what sells',
    'Promotions, suggested from your own shelves',
  ],
  premium: [
    'Everything in Pro',
    'Targets: break-even, monthly goal, treatment planning',
    'Unlimited products and people',
  ],
};

/** `Infinity` reads as "Unlimited" here; every other cap is its own number. */
function limitRow(limit: Record<Plan, number>) {
  const show = (p: Plan) =>
    limit[p] === Number.POSITIVE_INFINITY ? 'Unlimited' : String(limit[p]);
  return { starter: show('starter'), pro: show('pro'), premium: show('premium') };
}

/**
 * The comparison table. Every row is something the product actually does today
 * — see the barcode note at the top of this file.
 */
export const COMPARISON: {
  label: string;
  starter: string | boolean;
  pro: string | boolean;
  premium: string | boolean;
}[] = [
  // Limits are read from core rather than typed out. A comparison table that
  // quotes a cap the API does not enforce is a promise the product breaks, and
  // it is the kind of drift nobody notices until a customer hits it.
  { label: 'Products tracked', ...limitRow(PRODUCT_LIMIT) },
  { label: 'People', ...limitRow(SEAT_LIMIT) },
  { label: 'Batch & expiry tracking (FEFO)', starter: true, pro: true, premium: true },
  { label: 'Stock counts & movement ledger', starter: true, pro: true, premium: true },
  { label: 'Lot traceability', starter: true, pro: true, premium: true },
  { label: 'Spreadsheet import & export', starter: true, pro: true, premium: true },
  { label: 'Expiry alerts by email', starter: true, pro: true, premium: true },
  { label: 'Analytics', starter: false, pro: true, premium: true },
  { label: 'Promotions', starter: false, pro: true, premium: true },
  { label: 'Monthly targets & break-even', starter: false, pro: false, premium: true },
];

export function PricingSection() {
  const [cycle, setCycle] = useState<Cycle>('monthly');

  return (
    <section
      id="pricing"
      style={{
        background: c.appBg,
        padding: '64px 24px 72px',
        fontFamily: sans,
        color: c.ink,
      }}
    >
      <div style={{ maxWidth: 1040, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 30 }}>
          <h2 style={{ fontFamily: sans, letterSpacing: '-0.03em', fontWeight: 300, fontSize: 34, margin: '0 0 10px' }}>
            Simple pricing, no card to start
          </h2>
          <p
            style={{
              fontSize: 15,
              color: c.muted,
              margin: '0 auto 20px',
              maxWidth: 560,
              lineHeight: 1.7,
            }}
          >
            Every plan includes a {TRIAL_DAYS}-day free trial. No card is needed to try it, and you
            can change plan or cancel whenever you like.
          </p>

          <div
            style={{ ...segTrack, display: 'inline-flex' }}
          >
            {(['monthly', 'yearly'] as Cycle[]).map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => setCycle(k)}
                style={segTab(cycle === k)}
              >
                {k === 'monthly' ? 'Monthly' : 'Yearly'}
              </button>
            ))}
          </div>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: 16,
            marginBottom: 44,
          }}
        >
          {PLANS.map((plan) => {
            const p = PRICING[plan];
            // Pro is the plan most clinics should be on: it is the first with
            // analytics, which is the reason most of them are looking.
            const featured = plan === 'pro';
            return (
              <div
                key={plan}
                style={{
                  background: featured ? c.greenDeep : c.white,
                  color: featured ? c.onDarkBright : c.ink,
                  border: `1px solid ${featured ? c.greenDeep : c.border}`,
                  borderRadius: 18,
                  padding: '22px 20px',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                  <strong style={{ fontSize: 16 }}>{PLAN_LABEL[plan]}</strong>
                  {featured && (
                    <span
                      style={{
                        fontSize: 9.5,
                        fontWeight: 600,
                        letterSpacing: '0.6px',
                        color: c.onDarkSoft,
                        border: `1px solid ${c.borderDark}`,
                        borderRadius: 980,
                        padding: '2px 8px',
                      }}
                    >
                      Most popular
                    </span>
                  )}
                </div>

                <div style={{ fontFamily: sans, letterSpacing: '-0.03em', fontSize: 36, fontWeight: 300, margin: '12px 0 2px' }}>
                  {money(cycle === 'yearly' ? p.yearlyPerMonth : p.monthly)}
                  <span
                    style={{
                      fontSize: 14,
                      fontFamily: sans,
                      color: featured ? c.onDarkMuted : c.muted,
                    }}
                  >
                    {' '}
                    /month
                  </span>
                </div>
                <div
                  style={{
                    fontSize: 12.5,
                    color: featured ? c.onDarkMuted : c.muted2,
                    minHeight: 18,
                  }}
                >
                  {cycle === 'yearly'
                    ? `${money(p.yearlyTotal)} billed yearly · save ${money(yearlySaving(plan))}`
                    : 'billed monthly'}
                </div>

                <ul
                  style={{
                    listStyle: 'none',
                    margin: '18px 0 0',
                    padding: 0,
                    fontSize: 13,
                    lineHeight: 1.85,
                    color: featured ? c.onDarkMuted : c.muted,
                    flex: 1,
                  }}
                >
                  {HIGHLIGHTS[plan].map((f) => (
                    <li key={f} style={{ display: 'flex', gap: 8 }}>
                      <span style={{ color: featured ? c.onDarkSoft : c.green }}>✓</span>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <div
                  style={{
                    marginTop: 18,
                    paddingTop: 14,
                    borderTop: `1px solid ${featured ? c.borderDark : c.border}`,
                    fontSize: 12,
                    color: featured ? c.onDarkFaint : c.faint,
                  }}
                >
                  {PRODUCT_LIMIT[plan] === Number.POSITIVE_INFINITY
                    ? 'Unlimited products'
                    : `Up to ${PRODUCT_LIMIT[plan]} products`}
                  {' · '}
                  {SEAT_LIMIT[plan] === Number.POSITIVE_INFINITY
                    ? 'unlimited people'
                    : `${SEAT_LIMIT[plan]} people`}
                </div>
              </div>
            );
          })}
        </div>

        <h3
          style={{
            fontFamily: sans, letterSpacing: '-0.03em',
            fontWeight: 300,
            fontSize: 23,
            margin: '0 0 16px',
            textAlign: 'center',
          }}
        >
          Compare every plan
        </h3>

        <div style={{ overflowX: 'auto' }}>
          <table
            style={{
              width: '100%',
              minWidth: 520,
              borderCollapse: 'collapse',
              background: c.white,
              border: `1px solid ${c.border}`,
              borderRadius: 12,
            }}
          >
            <thead>
              <tr>
                <th style={{ ...th, textAlign: 'left', width: '40%' }} />
                {PLANS.map((p) => (
                  <th key={p} style={th}>
                    {PLAN_LABEL[p]}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {COMPARISON.map((row) => (
                <tr key={row.label} style={{ borderTop: `1px solid ${c.border}` }}>
                  <td style={{ ...td, textAlign: 'left', color: c.ink }}>{row.label}</td>
                  {(['starter', 'pro', 'premium'] as Plan[]).map((p) => (
                    <td key={p} style={td}>
                      <Cell value={row[p]} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p
          style={{
            fontSize: 12.5,
            color: c.faint,
            textAlign: 'center',
            margin: '20px auto 0',
            maxWidth: 620,
            lineHeight: 1.7,
          }}
        >
          No VAT is added to these prices. Payments and invoices are handled by
          Stripe. We never see your card details. Your data stays yours: export everything to a
          spreadsheet at any time.
        </p>
      </div>
    </section>
  );
}

const th: React.CSSProperties = {
  padding: '12px 14px',
  fontSize: 12.5,
  textTransform: 'lowercase',
  color: c.muted2,
  fontWeight: 400,
  textAlign: 'center',
};

const td: React.CSSProperties = {
  padding: '11px 14px',
  fontSize: 13.5,
  textAlign: 'center',
  color: c.muted,
};

function Cell({ value }: { value: string | boolean }) {
  if (value === true) return <span style={{ color: c.success, fontSize: 15 }}>✓</span>;
  // An em dash rather than a cross: the row is absent from this plan, not
  // broken in it, and a red ✗ reads as a fault.
  if (value === false) return <span style={{ color: c.faint }}>—</span>;
  return <span style={{ color: c.ink, fontWeight: 500 }}>{value}</span>;
}
